import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Download, Gift, Sparkles, Ticket, Award, Settings, ExternalLink, AlertTriangle, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import QRCode from 'react-qr-code';
import { useAuth } from '../../context/AuthContext';
import { getUserGames, createGame, updateGame, updateGameSettings } from '../../services/firestore';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GamesPage = () => {
  const { currentUser } = useAuth();
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [gameToToggle, setGameToToggle] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(null);
  const [copiedGameId, setCopiedGameId] = useState(null);
  const [wheelPrizes, setWheelPrizes] = useState([
    { name: "10% Off", probability: 30, color: "#60a5fa" },
    { name: "20% Off", probability: 20, color: "#34d399" },
    { name: "30% Off", probability: 10, color: "#fbbf24" },
    { name: "Try Again", probability: 40, color: "#94a3b8" }
  ]);
  const [promoCode, setPromoCode] = useState("WELCOME2024");

  const copyToClipboard = async (url, gameId) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedGameId(gameId);
      toast.success('Lien copié !');
      setTimeout(() => setCopiedGameId(null), 2000);
    } catch (err) {
      toast.error('Erreur lors de la copie');
    }
  };

  const gameTypes = [
    {
      id: 'wheel',
      name: 'Roue de la Fortune',
      icon: <Award className="w-8 h-8" />,
      description: 'Faites tourner la roue pour gagner des prix',
      color: 'from-blue-500 to-purple-600'
    },
    {
      id: 'promo',
      name: 'Code Promo',
      icon: <Sparkles className="w-8 h-8" />,
      description: 'Obtenez un code promo instantané',
      color: 'from-pink-500 to-rose-600'
    }
  ];

  useEffect(() => {
    if (currentUser) {
      fetchGames();
    }
  }, [currentUser]);

  const fetchGames = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      // Le backend crée automatiquement les jeux par défaut s'ils n'existent pas
      const gamesData = await getUserGames(currentUser.uid);
      setGames(gamesData);
      
      // Load settings for each game
      gamesData.forEach(game => {
        if (game.game_type === 'wheel' && game.game_settings?.prizes) {
          setWheelPrizes(game.game_settings.prizes);
        }
        if (game.game_type === 'promo' && game.game_settings?.promo_code) {
          setPromoCode(game.game_settings.promo_code);
        }
      });
    } catch (error) {
      console.error('Error fetching games:', error);
      toast.error('Erreur lors du chargement des jeux');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleGame = async (gameType) => {
    const game = games.find(g => g.game_type === gameType);
    const newStatus = !game?.is_active;

    // If deactivating, show confirmation
    if (game?.is_active) {
      setGameToToggle({ gameType, newStatus });
      setShowConfirm(true);
      return;
    }

    // If activating, proceed directly
    await toggleGame(gameType, newStatus);
  };

  const toggleGame = async (gameType, newStatus) => {
    if (!currentUser) return;

    try {
      const gameId = `${currentUser.uid}_${gameType}`;
      const FRONTEND_URL = window.location.origin; // Use current domain
      
      if (newStatus) {
        // Generate game URL
        const game_url = `${FRONTEND_URL}/game/${gameType}/${currentUser.uid}`;
        
        // Generate QR code data URL (simple text QR for now)
        const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(game_url)}`;
        
        await updateGame(gameId, {
          is_active: true,
          qr_code_url: qrCodeUrl,
          game_url: game_url
        });
      } else {
        await updateGame(gameId, {
          is_active: false,
          qr_code_url: null,
          game_url: null
        });
      }

      await fetchGames();
      toast.success(newStatus ? 'Jeu activé avec QR code généré!' : 'Jeu désactivé!');
      setShowConfirm(false);
      setGameToToggle(null);
    } catch (error) {
      console.error('Error toggling game:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleSaveWheelSettings = async () => {
    if (!currentUser) return;
    
    // Validate total probability = 100
    const total = wheelPrizes.reduce((sum, p) => sum + p.probability, 0);
    if (total !== 100) {
      toast.error(`Total des probabilités doit être 100% (actuellement ${total}%)`);
      return;
    }

    try {
      await updateGameSettings(currentUser.uid, 'wheel', { prizes: wheelPrizes });
      toast.success('Paramètres de la roue sauvegardés!');
      setSettingsOpen(null);
      fetchGames();
    } catch (error) {
      console.error('Error saving wheel settings:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleSavePromoSettings = async () => {
    if (!currentUser) return;

    try {
      await updateGameSettings(currentUser.uid, 'promo', { promo_code: promoCode });
      toast.success('Code promo sauvegardé!');
      setSettingsOpen(null);
      fetchGames();
    } catch (error) {
      console.error('Error saving promo settings:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const downloadQRCode = (gameUrl, gameName) => {
    const svg = document.getElementById(`qr-${gameName}`);
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL('image/png');
      
      const downloadLink = document.createElement('a');
      downloadLink.download = `${gameName}-qr-code.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-[#2E59D7]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="games-page">
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Jeu & Fidélisation</h2>
        <p className="text-gray-600">Gérez vos jeux interactifs et engagez vos clients</p>
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-orange-600">
              <AlertTriangle className="w-6 h-6" />
              Attention
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-gray-700 mb-4">
              Es-tu sûr de vouloir désactiver cette page ?
            </p>
            <p className="text-gray-600 text-sm bg-orange-50 p-4 rounded-lg border border-orange-200">
              ⚠️ Si les QR codes sont déjà imprimés et déployés dans ton local, ils ne fonctionneront plus.
            </p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowConfirm(false)} className="flex-1">
              Annuler
            </Button>
            <Button 
              onClick={() => toggleGame(gameToToggle.gameType, gameToToggle.newStatus)} 
              className="flex-1 bg-orange-600 hover:bg-orange-700"
            >
              Désactiver quand même
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Games Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {gameTypes.map((gameType) => {
          const game = games.find(g => g.game_type === gameType.id);
          const isActive = game?.is_active || false;
          const gameUrl = game?.game_url;

          return (
            <Card key={gameType.id} className={`p-6 border-2 ${isActive ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-start gap-4">
                  <div className={`w-16 h-16 bg-gradient-to-br ${gameType.color} rounded-2xl flex items-center justify-center text-white`}>
                    {gameType.icon}
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">{gameType.name}</h3>
                    <p className="text-sm text-gray-600">{gameType.description}</p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {/* Settings Button */}
                  {isActive && (
                    <Dialog open={settingsOpen === gameType.id} onOpenChange={(open) => setSettingsOpen(open ? gameType.id : null)}>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" className="text-gray-600">
                          <Settings className="w-5 h-5" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl">
                        <DialogHeader>
                          <DialogTitle>Paramètres - {gameType.name}</DialogTitle>
                        </DialogHeader>
                        
                        {gameType.id === 'wheel' && (
                          <div className="space-y-4 py-4">
                            <p className="text-sm text-gray-600">Configurez les prix et leurs probabilités (total = 100%)</p>
                            {wheelPrizes.map((prize, idx) => {
                              const isLostPrize = prize.name.toLowerCase() === 'perdu';
                              return (
                                <div key={idx} className="grid grid-cols-12 gap-3 items-center">
                                  <div className="col-span-5">
                                    <Label>Prix {idx + 1} {isLostPrize && <span className="text-xs text-gray-500">(non modifiable)</span>}</Label>
                                    <Input
                                      value={prize.name}
                                      onChange={(e) => {
                                        if (!isLostPrize) {
                                          const newPrizes = [...wheelPrizes];
                                          newPrizes[idx].name = e.target.value;
                                          setWheelPrizes(newPrizes);
                                        }
                                      }}
                                      disabled={isLostPrize}
                                      className={`mt-1 ${isLostPrize ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    />
                                  </div>
                                  <div className="col-span-3">
                                    <Label>Probabilité %</Label>
                                    <Input
                                      type="number"
                                      min="0"
                                      max="100"
                                      value={prize.probability}
                                      onChange={(e) => {
                                        const newPrizes = [...wheelPrizes];
                                        newPrizes[idx].probability = parseInt(e.target.value) || 0;
                                        setWheelPrizes(newPrizes);
                                      }}
                                      className="mt-1"
                                    />
                                  </div>
                                  <div className="col-span-4">
                                    <Label>Couleur</Label>
                                    <Input
                                      type="color"
                                      value={prize.color}
                                      onChange={(e) => {
                                        const newPrizes = [...wheelPrizes];
                                        newPrizes[idx].color = e.target.value;
                                        setWheelPrizes(newPrizes);
                                      }}
                                      className="mt-1 h-10"
                                    />
                                  </div>
                                </div>
                              );
                            })}
                            <div className="bg-blue-50 p-3 rounded">
                              <p className="text-sm font-semibold">
                                Total: {wheelPrizes.reduce((sum, p) => sum + p.probability, 0)}%
                              </p>
                            </div>
                            <Button onClick={handleSaveWheelSettings} className="w-full bg-gradient-to-r from-[#2E59D7] to-[#0082FB]">
                              Sauvegarder les paramètres
                            </Button>
                          </div>
                        )}

                        {gameType.id === 'promo' && (
                          <div className="space-y-4 py-4">
                            <div>
                              <Label>Code Promo à afficher</Label>
                              <Input
                                value={promoCode}
                                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                                placeholder="WELCOME2024"
                                className="mt-1 text-2xl font-bold text-center"
                              />
                              <p className="text-sm text-gray-500 mt-2">Ce code sera affiché aux clients</p>
                            </div>
                            <Button onClick={handleSavePromoSettings} className="w-full bg-gradient-to-r from-[#2E59D7] to-[#0082FB]">
                              Sauvegarder le code
                            </Button>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  )}

                  {/* Toggle Switch */}
                  <button
                    onClick={() => handleToggleGame(gameType.id)}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors ${
                      isActive ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                    data-testid={`toggle-${gameType.id}`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        isActive ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {isActive && (
                <div className="mt-6 space-y-4">
                  {/* QR Code Display */}
                  {gameUrl ? (
                    <div className="flex items-start gap-4">
                      <div className="bg-white p-4 rounded-lg border-2 border-gray-200">
                        <QRCode
                          id={`qr-${gameType.id}`}
                          value={gameUrl}
                          size={120}
                          level="H"
                        />
                      </div>

                      <div className="flex-1 space-y-2">
                        <p className="text-sm text-gray-600">
                          <strong>Lien du jeu:</strong>
                        </p>
                        <div className="flex items-center gap-2 bg-blue-50 p-3 rounded-lg border border-blue-200">
                          <ExternalLink className="w-4 h-4 text-[#2E59D7] flex-shrink-0" />
                          <a 
                            href={gameUrl} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-[#2E59D7] font-medium truncate flex-1 hover:underline"
                          >
                            {gameUrl}
                          </a>
                          <button
                            onClick={() => copyToClipboard(gameUrl, game?.id)}
                            className="p-1.5 hover:bg-blue-100 rounded transition-colors flex-shrink-0"
                            title="Copier le lien"
                          >
                            {copiedGameId === game?.id ? (
                              <Check className="w-4 h-4 text-green-600" />
                            ) : (
                              <Copy className="w-4 h-4 text-[#2E59D7]" />
                            )}
                          </button>
                        </div>

                        <div className="flex gap-2 mt-3">
                          <Button
                            size="sm"
                            onClick={() => copyToClipboard(gameUrl, game?.id)}
                            variant="outline"
                            className="border-[#2E59D7] text-[#2E59D7] hover:bg-blue-50"
                          >
                            {copiedGameId === game?.id ? (
                              <>
                                <Check className="w-4 h-4 mr-1" />
                                Copié !
                              </>
                            ) : (
                              <>
                                <Copy className="w-4 h-4 mr-1" />
                                Copier le lien
                              </>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => downloadQRCode(gameUrl, gameType.id)}
                            className="bg-gradient-to-r from-[#2E59D7] to-[#0082FB]"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Télécharger QR
                          </Button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <p className="text-sm">Activation du jeu en cours...</p>
                    </div>
                  )}

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 pt-4 border-t">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{game?.total_scans || 0}</p>
                      <p className="text-xs text-gray-600">Scans totaux</p>
                    </div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gray-900">{game?.total_plays || 0}</p>
                      <p className="text-xs text-gray-600">Parties jouées</p>
                    </div>
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Instructions */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-sky-50">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Comment ça marche?</h3>
        <ol className="space-y-3 text-gray-700">
          <li className="flex gap-3">
            <span className="font-bold text-[#2E59D7]">1.</span>
            <span>Activez le(s) jeu(x) de votre choix avec le bouton</span>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-[#2E59D7]">2.</span>
            <span>Générez et téléchargez le QR code</span>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-[#2E59D7]">3.</span>
            <span>Affichez le QR code dans votre établissement</span>
          </li>
          <li className="flex gap-3">
            <span className="font-bold text-[#2E59D7]">4.</span>
            <span>Vos clients scannent et jouent pour gagner des prix!</span>
          </li>
        </ol>
      </Card>
    </div>
  );
};

export default GamesPage;
